import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { app, firebaseMeasurementId } from '../firebase';

let analytics = null;
let initialized = false;
const pendingEvents = [];
let consentMode = 'denied'; // 'denied' | 'granted' | 'admin_bypass'

function canTrack() {
  return consentMode === 'granted' || consentMode === 'admin_bypass';
}

export function setAnalyticsConsentMode(mode) {
  consentMode = mode;

  if (canTrack() && !initialized) {
    initAnalytics();
  }
}

function sanitizeParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value != null && value !== '')
  );
}

function dispatchEvent(name, params = {}) {
  if (typeof window === 'undefined' || !canTrack()) return;

  const payload = sanitizeParams(params);

  if (initialized && analytics) {
    logEvent(analytics, name, payload);
    return;
  }

  pendingEvents.push({ name, params: payload });
}

export function initAnalytics() {
  if (typeof window === 'undefined' || initialized || !canTrack()) {
    return Promise.resolve(analytics);
  }

  return isSupported()
    .then((supported) => {
      if (!supported) return null;

      analytics = getAnalytics(app);
      initialized = true;

      // Performance Monitoring: só ativa se o produto estiver ok no console Firebase.
      // Em falha de envio o SDK logava "Tries left" em loop — desligado por padrão.
      const enablePerf =
        process.env.REACT_APP_FIREBASE_PERFORMANCE === 'true' ||
        process.env.REACT_APP_FIREBASE_PERFORMANCE === '1';
      if (enablePerf) {
        try {
          const perf = getPerformance(app);
          perf.dataCollectionEnabled = true;
          perf.instrumentationEnabled = true;
        } catch (_) {
          // Performance Monitoring pode não estar habilitado no projeto Firebase.
        }
      }

      pendingEvents.splice(0).forEach(({ name, params }) => {
        logEvent(analytics, name, params);
      });

      return analytics;
    })
    .catch(() => null);
}

export function trackPageView(path, title = '') {
  dispatchEvent('page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
}

export function trackPropertyView(property) {
  if (!property?.id) return;

  dispatchEvent('view_item', {
    item_id: String(property.id),
    item_name: property.title || property.code || '',
    item_category: property.type || property.propertyType || 'property',
    price: property.price || property.rentPrice || undefined,
    currency: 'BRL',
  });
}

export function trackCondominiumView(condominium) {
  if (!condominium?.id) return;

  dispatchEvent('view_item', {
    item_id: String(condominium.id),
    item_name: condominium.name || condominium.title || '',
    item_category: 'condominium',
  });
}

export function trackGenerateLead({ source, itemId, itemType, landingSlug } = {}) {
  dispatchEvent('generate_lead', {
    source: source || 'unknown',
    item_id: itemId ? String(itemId) : undefined,
    item_type: itemType,
    landing_slug: landingSlug,
  });
}

export function trackWhatsAppClick({ source, itemId, itemType } = {}) {
  dispatchEvent('whatsapp_click', {
    source: source || 'unknown',
    item_id: itemId ? String(itemId) : undefined,
    item_type: itemType,
  });
}

export function trackSearch({ searchTerm, filters } = {}) {
  dispatchEvent('search', {
    search_term: searchTerm,
    filters: filters ? JSON.stringify(filters).slice(0, 100) : undefined,
  });
}

export function trackWebVital({ name, value, rating, delta, id }) {
  dispatchEvent('web_vitals', {
    metric_name: name,
    metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
    metric_rating: rating,
    metric_delta: delta != null ? Math.round(delta) : undefined,
    metric_id: id,
  });
}

export function trackLogin(method = 'email') {
  dispatchEvent('login', { method });
}

export { firebaseMeasurementId };
